/*
 * The authoring draft: a template as a form rather than as a JSON document.
 *
 * The old drawer edited Meta's `components` array in a textarea, so authoring a
 * template meant knowing Meta's component schema by heart. Buttons in
 * particular were reachable only by hand-writing them, and nothing on the page
 * hinted they existed.
 *
 * This module is the one place that knows how a filled-in form becomes Meta's
 * components and how an existing template becomes a filled-in form. Both
 * directions live together on purpose: a round trip that loses something is a
 * bug you can only see by reading the two halves side by side.
 *
 * `componentsFromDraft` deliberately builds the SAME shape the service builds
 * for a library entry (`TemplateLibraryCatalog.BuildComponents`), so a template
 * started from the library and one written by hand are indistinguishable
 * afterwards.
 */
import { parseComponents, type ParsedComponent } from '../../../lib/template-components';
import { HEADER_FORMATS, type HeaderFormat } from '../components/HeaderBuilder';

/** The button types the form offers. Meta has more; see `lossless` below. */
export const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'] as const;
export type ButtonType = (typeof BUTTON_TYPES)[number];

export interface DraftButton {
  type: ButtonType;
  text: string;
  /** URL buttons only. May carry one `{{1}}` at the end. */
  url?: string;
  /** Call buttons only, in full international form. */
  phone_number?: string;
}

export interface TemplateDraft {
  /** As typed. `normaliseName` is what will actually be sent. */
  name: string;
  language: string;
  category: string;
  headerFormat: HeaderFormat;
  headerText: string;
  /** Media headers: the uploaded sample's URL. */
  headerSampleUrl: string;
  /** A text header with a placeholder needs its own example. */
  headerSample: string;
  body: string;
  footer: string;
  buttons: DraftButton[];
  /** Positional sample values for the body's placeholders: index 0 is {{1}}. */
  bodySamples: string[];
}

export function emptyDraft(): TemplateDraft {
  return {
    name: '',
    language: 'en_US',
    category: 'utility',
    headerFormat: 'NONE',
    headerText: '',
    headerSampleUrl: '',
    headerSample: '',
    body: '',
    footer: '',
    buttons: [],
    bodySamples: []
  };
}

// ── Names ────────────────────────────────────────────────────────────────────

/**
 * Meta accepts lower-case letters, digits and underscores in a template name
 * and nothing else. This is shown under the field as a person types, so the
 * name they will get is never a surprise, and it is what gets sent.
 *
 * Mirrors `TemplateNames.Normalise` in
 * `services/channel-wa/Services/TemplateValidator.cs`.
 */
export function normaliseName(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 512)
    .replace(/^_+|_+$/g, '');
}

// ── Placeholders ─────────────────────────────────────────────────────────────

const PLACEHOLDER = /\{\{\s*(\d+)\s*\}\}/g;

/** The highest placeholder position in a piece of text, or 0. */
export function highestPlaceholder(text: string): number {
  let max = 0;
  for (const m of text.matchAll(PLACEHOLDER)) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

/**
 * Add the next placeholder to the body.
 *
 * Numbering is positional and Meta refuses a gap, so the next one is always
 * "one more than the highest already there" rather than "one more than the
 * count": deleting {{2}} out of three and then adding one must give {{3}}
 * back, not a second {{3}} beside the existing one.
 */
export function appendPlaceholder(draft: TemplateDraft): TemplateDraft {
  const next = highestPlaceholder(draft.body) + 1;
  return {
    ...draft,
    body: `${draft.body}{{${next}}}`,
    bodySamples: resizeSamples(draft.bodySamples, next)
  };
}

/** Samples are positional, so the array is grown and shrunk, never reordered. */
export function resizeSamples(samples: string[], count: number): string[] {
  const next = samples.slice(0, count);
  while (next.length < count) next.push('');
  return next;
}

// ── Form → Meta ──────────────────────────────────────────────────────────────

/**
 * The draft as Meta's `components` array, pretty-printed.
 *
 * The examples are not decoration. Meta reviews a template against its sample
 * values, and a template whose placeholders have none is either refused or
 * reviewed against samples the service invented ("Sample 1"). Collecting them
 * in the form is the whole reason the sample fields exist.
 */
export function componentsFromDraft(draft: TemplateDraft): string {
  const components: Record<string, unknown>[] = [];

  if (draft.headerFormat !== 'NONE') {
    const header: Record<string, unknown> = { type: 'HEADER', format: draft.headerFormat };
    if (draft.headerFormat === 'TEXT') {
      header.text = draft.headerText;
      if (highestPlaceholder(draft.headerText) > 0 && draft.headerSample.trim()) {
        header.example = { header_text: [draft.headerSample.trim()] };
      }
    } else if (draft.headerSampleUrl.trim()) {
      header.example = { header_handle: [draft.headerSampleUrl.trim()] };
    }
    components.push(header);
  }

  const body: Record<string, unknown> = { type: 'BODY', text: draft.body };
  const wanted = highestPlaceholder(draft.body);
  if (wanted > 0) {
    const row = resizeSamples(draft.bodySamples, wanted);
    if (row.some((s) => s.trim())) body.example = { body_text: [row] };
  }
  components.push(body);

  if (draft.footer.trim()) components.push({ type: 'FOOTER', text: draft.footer.trim() });

  if (draft.buttons.length > 0) {
    components.push({
      type: 'BUTTONS',
      buttons: draft.buttons.map((b) => {
        const out: Record<string, unknown> = { type: b.type, text: b.text };
        if (b.type === 'URL') out.url = b.url ?? '';
        if (b.type === 'PHONE_NUMBER') out.phone_number = b.phone_number ?? '';
        return out;
      })
    });
  }

  return JSON.stringify(components, null, 2);
}

// ── Meta → form ──────────────────────────────────────────────────────────────

export interface ParsedDraft {
  draft: TemplateDraft;
  /**
   * False when the components carry something the form cannot express: a
   * component type it does not model, a button type it does not offer, an OTP
   * or flow button, a LOCATION header. The Content step then keeps the raw
   * editor rather than quietly dropping what it cannot show, which is the one
   * way a form editor can lose somebody's work.
   */
  lossless: boolean;
}

const KNOWN_COMPONENTS = new Set(['HEADER', 'BODY', 'FOOTER', 'BUTTONS']);

export function draftFromComponents(
  componentsJson: string | null | undefined,
  base: Partial<TemplateDraft> = {}
): ParsedDraft {
  const draft: TemplateDraft = { ...emptyDraft(), ...base };
  let lossless = true;

  let raw: unknown;
  try {
    raw = JSON.parse(componentsJson || '[]');
  } catch {
    return { draft, lossless: false };
  }
  if (!Array.isArray(raw)) return { draft, lossless: false };

  const parsed: ParsedComponent[] = parseComponents(componentsJson);

  for (const c of parsed) {
    if (!KNOWN_COMPONENTS.has(c.type)) { lossless = false; continue; }

    if (c.type === 'HEADER') {
      const format = (c.format ?? 'TEXT').toUpperCase();
      if (!(HEADER_FORMATS as readonly string[]).includes(format)) { lossless = false; continue; }
      draft.headerFormat = format as HeaderFormat;
      draft.headerText = c.text ?? '';
      draft.headerSampleUrl = c.example?.header_handle?.[0] ?? '';
    } else if (c.type === 'BODY') {
      draft.body = c.text ?? '';
    } else if (c.type === 'FOOTER') {
      draft.footer = c.text ?? '';
    } else if (c.type === 'BUTTONS') {
      for (const b of c.buttons ?? []) {
        const type = (b.type ?? 'QUICK_REPLY').toUpperCase();
        if (!(BUTTON_TYPES as readonly string[]).includes(type)) { lossless = false; continue; }
        draft.buttons.push({
          type: type as ButtonType,
          text: b.text ?? '',
          url: b.url,
          phone_number: b.phone_number
        });
      }
    }
  }

  // Samples travel with the wording, so an imported or library template keeps
  // the examples it arrived with rather than having them invented at submit.
  draft.bodySamples = resizeSamples(readBodySamples(raw), highestPlaceholder(draft.body));
  draft.headerSample = readHeaderSample(raw);

  return { draft, lossless };
}

/** `example.body_text[0]` on the BODY component. */
function readBodySamples(raw: unknown): string[] {
  for (const c of raw as Record<string, unknown>[]) {
    if (String(c?.type ?? '').toUpperCase() !== 'BODY') continue;
    const rows = (c.example as { body_text?: unknown } | undefined)?.body_text;
    if (!Array.isArray(rows) || !Array.isArray(rows[0])) return [];
    return (rows[0] as unknown[]).map((v) => (typeof v === 'string' ? v : ''));
  }
  return [];
}

/** `example.header_text[0]` on a TEXT header. */
function readHeaderSample(raw: unknown): string {
  for (const c of raw as Record<string, unknown>[]) {
    if (String(c?.type ?? '').toUpperCase() !== 'HEADER') continue;
    const values = (c.example as { header_text?: unknown } | undefined)?.header_text;
    if (Array.isArray(values) && typeof values[0] === 'string') return values[0];
  }
  return '';
}

/** Two drafts hold the same template. Used for the unsaved-work guard. */
export function sameDraft(a: TemplateDraft, b: TemplateDraft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
